import * as React from 'react';
import { useCallback, useMemo, useState, useEffect } from 'react';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyIcon from '@mui/icons-material/Key';
import {
  Box,
  Button,
  Dialog,
  InputAdornment,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  CircularProgress,
  AlertColor
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMessages } from 'modules/intl';
import { ConnectorInfo } from 'types/types';
import { postRequest } from 'utils/helper';
import { ErrorPage } from 'modules/common/routes/ErrorPage';
import { ToastSnackBar } from '../Setting/ToastSnackBar';
import { DialogHeaderContext } from './DialogHeader';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    marginTop: theme.spacing(2.5)
  },
  box: {
    display: 'flex',
    justifyContent: 'center'
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  }
}));

type ComputerEaseInstallPageProps = {
  info: ConnectorInfo;
  open: boolean;
  handleInstallDialogClose: () => void;
};

const messages = ['intermediateInstallDialog.inputError'];

export const ComputerEaseInstallPage: React.FC<ComputerEaseInstallPageProps> = ({
  info,
  open,
  handleInstallDialogClose
}: ComputerEaseInstallPageProps) => {
  const [textField, setTextField] = useState({
    apiKeyId: '',
    apiKey: '',
    refreshToken: ''
  });
  const [textFieldError, setTextFieldError] = useState({
    apiKeyIdError: '',
    apiKeyError: '',
    refreshTokenError: ''
  });
  const [connected, setConnected] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);

  const classes = useStyles();
  const errorMessage = useMessages(messages);
  const { formatMessage } = useIntl();

  const requiredInputError =
    errorMessage['intermediateInstallDialog.inputError'];

  const handleError = useCallback(
    (field: string, value: string) => {
      setTextFieldError((prev: any) => ({
        ...prev,
        [`${field}Error`]: value.trim() === '' ? requiredInputError : ''
      }));
    },
    [requiredInputError]
  );

  const handleChange = useCallback(
    (field: string, event: any) => {
      const value = event.target.value;
      setTextField((prev: any) => ({
        ...prev,
        [field]: value
      }));
      handleError(field, value);
    },
    [handleError]
  );

  const onSnackbarClose = useCallback(() => {
    setIsSnackBarOpen(false);
  }, []);
  const onSnackbarOpen = useCallback(() => {
    setIsSnackBarOpen(true);
  }, []);

  const handleApiCall = useCallback(async () => {
    setIsDisabled(true);
    setSeverity('success');
    onSnackbarOpen();
    try {
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        environment: 'sandbox',
        apiKeyId: textField.apiKeyId,
        apiKey: textField.apiKey,
        refreshToken: textField.refreshToken
      };
      const res = await postRequest(
        '/integration-settings-api/install-tokenbased-connector',
        body,
        true
      );
      if (res.data) {
        setConnected(true);
      }
    } catch (error: any) {
      if (error?.response?.status === 500) {
        setShowErrorPage(true);
      } else {
        setIsDisabled(false);
        setSeverity('error');
        onSnackbarOpen();
      }
    }
  }, [
    info.companyKey,
    info.shortName,
    textField.apiKeyId,
    textField.apiKey,
    textField.refreshToken,
    onSnackbarOpen
  ]);

  const validateAndConnect = (event: any) => {
    event.preventDefault();
    const value = event.target.value;

    let hasError = false;
    const fields: Array<keyof typeof textField> = [
      'apiKeyId',
      'apiKey',
      'refreshToken'
    ];

    fields.forEach((field) => {
      if (textField[field].trim() === '') {
        handleError(field, value);
        hasError = true;
      }
    });

    if (!hasError) {
      setIsDisabled(true);
      handleApiCall();
    }
  };

  /* eslint-disable indent */
  const alertMessage = useMemo(() => {
    return severity === 'error'
      ? formatMessage({
          id: 'intermediateInstallDialog.snackBarErrorMessage'
        })
      : formatMessage({
          id: 'intermediateInstallDialog.waitingMessage'
        });
  }, [formatMessage, severity]);

  useEffect(() => {
    if (connected) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [connected]);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleInstallDialogClose}
        data-testid="computerease-install-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          className={classes.header}
          data-testid="computerease-dialog-title"
        >
          <FormattedMessage id={'intermediateInstallDialog.header'} />
        </DialogTitle>
        <DialogContent>
          <DialogHeaderContext shortName={info.shortName} />
          <Box className={classes.box} data-testid="computerease-form-box">
            <div>
              <TextField
                id={'apiKeyId'}
                variant="outlined"
                required
                fullWidth
                value={textField.apiKeyId}
                onChange={(event) => handleChange('apiKeyId', event)}
                error={Boolean(textFieldError.apiKeyIdError)}
                label="API Key ID"
                type={'text'}
                helperText={
                  textFieldError.apiKeyIdError ? (
                    textFieldError.apiKeyIdError
                  ) : (
                    <FormattedMessage
                      id={
                        'intermediateOauthDialog.computerease.apiKeyIdHelperText'
                      }
                    />
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      data-testid="access-key-startAdornment"
                    >
                      <KeyIcon />
                    </InputAdornment>
                  )
                }}
                className={classes.textField}
                inputProps={{ 'data-testid': 'apiKeyIdInput' }}
              />

              <TextField
                id={'apiKey'}
                variant="outlined"
                required
                fullWidth
                value={textField.apiKey}
                onChange={(event) => handleChange('apiKey', event)}
                error={Boolean(textFieldError.apiKeyError)}
                label="API Key"
                type={'password'}
                helperText={
                  textFieldError.apiKeyError ? (
                    textFieldError.apiKeyError
                  ) : (
                    <FormattedMessage
                      id={
                        'intermediateOauthDialog.computerease.apiKeyHelperText'
                      }
                    />
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      data-testid="api-key-startAdornment"
                    >
                      <LockIcon />
                    </InputAdornment>
                  )
                }}
                className={classes.textField}
                inputProps={{ 'data-testid': 'apiKeyInput' }}
              />

              <TextField
                id={'refreshToken'}
                variant="outlined"
                required
                fullWidth
                value={textField.refreshToken}
                onChange={(event) => handleChange('refreshToken', event)}
                error={Boolean(textFieldError.refreshTokenError)}
                label="Refresh Token"
                type={'password'}
                helperText={
                  textFieldError.refreshTokenError ? (
                    textFieldError.refreshTokenError
                  ) : (
                    <FormattedMessage
                      id={
                        'intermediateOauthDialog.computerease.refreshTokenHelperText'
                      }
                    />
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      data-testid="refresh-token-startAdornment"
                    >
                      <RefreshIcon />
                    </InputAdornment>
                  )
                }}
                className={classes.textField}
                inputProps={{ 'data-testid': 'refreshTokenInput' }}
              />
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleInstallDialogClose}
            color="grey"
            autoFocus
            variant="contained"
            disabled={isDisabled}
            data-testid="cancel-button"
          >
            <FormattedMessage id={'connectorSetting.cancel'} />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={validateAndConnect}
            disabled={isDisabled}
            data-testid="connect-button"
          >
            {isDisabled ? (
              <Box display="flex" alignItems="center">
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                <FormattedMessage id="intermediateInstallDialog.installing" />
              </Box>
            ) : (
              <FormattedMessage
                id={'intermediateInstallDialog.connectButton'}
              />
            )}
          </Button>
        </DialogActions>
        <ToastSnackBar
          show={isSnackBarOpen}
          category={severity}
          message={alertMessage}
          onClose={onSnackbarClose}
        />
      </Dialog>
    </>
  );
};
